import { useState, useRef, useEffect, useMemo } from "react";
import { Search, SlidersHorizontal, MapPin, Calendar, Users, ChevronDown, X, RotateCcw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ANGOLA_PROVINCES, searchLocations, type LocationSuggestion } from "@/data/angola-locations";

const provinces = ANGOLA_PROVINCES;

interface CompactSearchBarProps {
  onOpenFilters?: () => void;
}

export function CompactSearchBar({ onOpenFilters }: CompactSearchBarProps = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const guestDropdownRef = useRef<HTMLDivElement>(null);
  const provinceDropdownRef = useRef<HTMLDivElement>(null);
  
  const [location, setLocation] = useState("");
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [guests, setGuests] = useState(2);
  const [province, setProvince] = useState("all");
  const [showProvinceDropdown, setShowProvinceDropdown] = useState(false);
  const [showGuestDropdown, setShowGuestDropdown] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [activeField, setActiveField] = useState<string | null>(null);

  // Autocomplete de endereço
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [highlightedSuggestion, setHighlightedSuggestion] = useState<number>(-1);
  const locationDropdownRef = useRef<HTMLDivElement>(null);

  const datePickerRef = useRef<HTMLDivElement>(null);

  const locationSuggestions = useMemo<LocationSuggestion[]>(
    () => searchLocations(location, 8),
    [location]
  );

  // Reset highlight quando a lista muda
  useEffect(() => {
    setHighlightedSuggestion(-1);
  }, [location]);

  // Fechar dropdowns ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (guestDropdownRef.current && !guestDropdownRef.current.contains(event.target as Node)) {
        setShowGuestDropdown(false);
      }
      if (provinceDropdownRef.current && !provinceDropdownRef.current.contains(event.target as Node)) {
        setShowProvinceDropdown(false);
      }
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setShowDatePicker(false);
      }
      if (locationDropdownRef.current && !locationDropdownRef.current.contains(event.target as Node)) {
        setShowLocationDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleGuestDropdown = () => {
    setShowGuestDropdown(!showGuestDropdown);
    setShowProvinceDropdown(false);
    setActiveField(showGuestDropdown ? null : "guests");
  };

  const toggleProvinceDropdown = () => {
    setShowProvinceDropdown(!showProvinceDropdown);
    setShowGuestDropdown(false);
    setShowDatePicker(false);
    setActiveField(showProvinceDropdown ? null : "province");
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();
    
    const days = [];
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  };

  const formatDateForInput = (day: number) => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const isDateDisabled = (day: number) => {
    const date = new Date();
    const year = date.getFullYear();
    const month = date.getMonth();
    const checkDate = new Date(year, month, day);
    return checkDate < new Date(date.setHours(0, 0, 0, 0));
  };

  const isDateSelected = (day: number, type: 'checkIn' | 'checkOut') => {
    const dateStr = formatDateForInput(day);
    return type === 'checkIn' ? dateStr === checkIn : dateStr === checkOut;
  };

  const monthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
  const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (location) params.set("location", location);
    if (checkIn) params.set("checkIn", checkIn);
    if (checkOut) params.set("checkOut", checkOut);
    if (guests > 0) params.set("guests", guests.toString());
    if (province && province !== "all") params.set("province", province);
    
    navigate(`/explorar?${params.toString()}`);
  };

  const hasAnyValue = Boolean(
    location || checkIn || checkOut || guests !== 2 || (province && province !== "all")
  );

  const handleResetAll = () => {
    setLocation("");
    setCheckIn("");
    setCheckOut("");
    setGuests(2);
    setProvince("all");
    setShowProvinceDropdown(false);
    setShowGuestDropdown(false);
    setShowDatePicker(false);
    setShowLocationDropdown(false);
    setActiveField(null);
    // Atualiza imediatamente os resultados removendo todos os params
    navigate("/explorar");
  };

  const handleSelectSuggestion = (suggestion: LocationSuggestion) => {
    setLocation(suggestion.fullName);
    setShowLocationDropdown(false);
    // Tenta pré-selecionar a província correspondente
    const matchedProvince = provinces.find(
      (p) => p.name.toLowerCase() === suggestion.province.toLowerCase()
    );
    if (matchedProvince) {
      setProvince(matchedProvince.id);
    }
    // Atualiza resultados imediatamente
    const params = new URLSearchParams();
    params.set("location", suggestion.fullName);
    if (matchedProvince && matchedProvince.id !== "all") {
      params.set("province", matchedProvince.id);
    }
    if (checkIn) params.set("checkIn", checkIn);
    if (checkOut) params.set("checkOut", checkOut);
    if (guests > 0) params.set("guests", guests.toString());
    navigate(`/explorar?${params.toString()}`);
  };

  const handleLocationKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showLocationDropdown || locationSuggestions.length === 0) {
      if (e.key === "Enter") {
        handleSearch();
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedSuggestion((prev) =>
        prev < locationSuggestions.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedSuggestion((prev) =>
        prev > 0 ? prev - 1 : locationSuggestions.length - 1
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedSuggestion >= 0) {
        handleSelectSuggestion(locationSuggestions[highlightedSuggestion]);
      } else {
        handleSearch();
      }
    } else if (e.key === "Escape") {
      setShowLocationDropdown(false);
    }
  };

  const handleFilterClick = () => {
    if (onOpenFilters) {
      onOpenFilters();
    } else {
      navigate("/explorar?filters=true");
    }
  };

  const toggleDatePicker = () => {
    setShowDatePicker(!showDatePicker);
    setShowGuestDropdown(false);
    setShowProvinceDropdown(false);
  };

  const handleDateSelect = (type: 'checkIn' | 'checkOut', date: string) => {
    if (type === 'checkIn') {
      setCheckIn(date);
      if (checkOut && new Date(date) > new Date(checkOut)) {
        setCheckOut('');
      }
    } else {
      if (checkIn && new Date(date) >= new Date(checkIn)) {
        setCheckOut(date);
      } else if (!checkIn) {
        setCheckOut(date);
      }
    }
  };

  const clearDates = () => {
    setCheckIn('');
    setCheckOut('');
  };

  const applyDates = () => {
    setShowDatePicker(false);
  };

  const getProvinceName = () => {
    const p = provinces.find(p => p.id === province);
    return p?.name || t('search.all');
  };

  const formatDates = () => {
    if (!checkIn && !checkOut) return t('search.addDates');
    if (checkIn && !checkOut) return new Date(checkIn).toLocaleDateString("pt-AO", { day: "numeric", month: "short" });
    if (checkIn && checkOut) {
      const start = new Date(checkIn).toLocaleDateString("pt-AO", { day: "numeric", month: "short" });
      const end = new Date(checkOut).toLocaleDateString("pt-AO", { day: "numeric", month: "short" });
      return `${start} - ${end}`;
    }
    return t('search.addDates');
  };

  return (
    <div className="w-full flex justify-center">
      <div className="w-full max-w-[900px] bg-white rounded-full shadow-[0_2px_20px_rgba(0,0,0,0.08)] border border-gray-100 flex items-stretch h-[64px] hover:shadow-[0_4px_24px_rgba(232,81,61,0.12)] transition-all duration-300 ease-out">
        
        {/* ONDE - Location */}
        <div
          ref={locationDropdownRef}
          className={`flex-[2.2] min-w-[160px] px-5 flex flex-col justify-center cursor-pointer transition-all duration-200 rounded-l-full hover:bg-gray-50/80 relative ${activeField === 'location' ? 'bg-gray-50/80' : ''}`}
          onClick={() => {
            setActiveField('location');
            if (location) setShowLocationDropdown(true);
          }}
        >
          <label className="block text-[11px] font-bold text-[#E8513D] uppercase tracking-wider mb-1">
            {t('home.search.where')}
          </label>
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <input
              type="text"
              placeholder={t('search.addressPlaceholder')}
              value={location}
              onChange={(e) => {
                setLocation(e.target.value);
                setShowLocationDropdown(e.target.value.length > 0);
              }}
              onFocus={() => {
                setActiveField('location');
                if (location) setShowLocationDropdown(true);
              }}
              onKeyDown={handleLocationKeyDown}
              autoComplete="off"
              role="combobox"
              aria-expanded={showLocationDropdown}
              aria-controls="location-suggestions"
              aria-autocomplete="list"
              className="w-full bg-transparent text-[15px] text-gray-700 placeholder-gray-400 outline-none truncate font-normal"
            />
            {location && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setLocation("");
                  setShowLocationDropdown(false);
                }}
                className="flex-shrink-0 w-5 h-5 rounded-full hover:bg-gray-200 flex items-center justify-center transition-colors"
                aria-label={t('search.clearAddress')}
              >
                <X className="w-3 h-3 text-gray-400" />
              </button>
            )}
          </div>

          {/* Autocomplete Dropdown */}
          {showLocationDropdown && locationSuggestions.length > 0 && (
            <div
              id="location-suggestions"
              role="listbox"
              className="absolute top-[calc(100%+12px)] left-0 w-[360px] max-w-[90vw] bg-white rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] border border-gray-100 py-2 z-[100] animate-in fade-in zoom-in-95 duration-200 max-h-[360px] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-4 py-1.5 text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                {t('search.suggestions')}
              </div>
              {locationSuggestions.map((suggestion, index) => (
                <button
                  key={suggestion.id}
                  type="button"
                  role="option"
                  aria-selected={index === highlightedSuggestion}
                  onMouseEnter={() => setHighlightedSuggestion(index)}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectSuggestion(suggestion);
                  }}
                  className={`w-full px-4 py-2.5 text-left flex items-center gap-3 transition-colors duration-150 ${
                    index === highlightedSuggestion
                      ? "bg-[#E8513D]/5"
                      : "hover:bg-gray-50"
                  }`}
                >
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    index === highlightedSuggestion ? "bg-[#E8513D]/10" : "bg-gray-100"
                  }`}>
                    <MapPin className={`w-4 h-4 ${
                      index === highlightedSuggestion ? "text-[#E8513D]" : "text-gray-500"
                    }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[14px] font-semibold text-gray-900 truncate">
                      {suggestion.name}
                    </div>
                    <div className="text-[12px] text-gray-500 truncate capitalize">
                      {suggestion.type} · {suggestion.province}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="w-px h-8 bg-gray-200 self-center" />

        {/* QUANDO - Dates */}
        <div 
          ref={datePickerRef}
          className="flex-1 min-w-[130px] px-4 flex flex-col justify-center cursor-pointer transition-all duration-200 hover:bg-gray-50/80 hidden sm:flex relative"
          onClick={toggleDatePicker}
        >
          <label className="block text-[11px] font-bold text-[#E8513D] uppercase tracking-wider mb-1">
            {t('home.search.when')}
          </label>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <span className="text-[15px] text-gray-600 truncate">
              {formatDates()}
            </span>
          </div>
          
          {/* Date Picker Dropdown */}
          {showDatePicker && (
            <div className="absolute top-[calc(100%+12px)] left-1/2 -translate-x-1/2 w-[340px] bg-white rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] border border-gray-100 p-5 z-[100] animate-in fade-in zoom-in-95 duration-200">
              <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white border-l border-t border-gray-100 rotate-45" />
              
              <div className="flex items-center justify-between mb-4">
                <span className="text-base font-semibold text-gray-900">
                  {monthNames[new Date().getMonth()]} {new Date().getFullYear()}
                </span>
                <div className="flex items-center gap-2">
                  {(checkIn || checkOut) && (
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        clearDates();
                      }}
                      className="text-sm text-gray-400 hover:text-[#E8513D] transition-colors"
                    >
                      {t('search.clear')}
                    </button>
                  )}
                </div>
              </div>
              
              {/* Week days header */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {weekDays.map(day => (
                  <div key={day} className="text-center text-xs font-medium text-gray-400 py-1">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-1">
                {generateCalendarDays().map((day, index) => (
                  <button
                    key={index}
                    disabled={day === null || (day && isDateDisabled(day))}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (day) {
                        if (!checkIn || (checkIn && checkOut)) {
                          handleDateSelect('checkIn', formatDateForInput(day));
                          setCheckOut('');
                        } else {
                          handleDateSelect('checkOut', formatDateForInput(day));
                        }
                      }
                    }}
                    className={`
                      w-9 h-9 rounded-full text-sm font-medium transition-all duration-200
                      ${day === null ? 'invisible' : ''}
                      ${day && isDateDisabled(day) ? 'text-gray-300 cursor-not-allowed' : ''}
                      ${day && isDateSelected(day, 'checkIn') ? 'bg-[#E8513D] text-white' : ''}
                      ${day && isDateSelected(day, 'checkOut') ? 'bg-[#E8513D] text-white' : ''}
                      ${day && !isDateDisabled(day) && !isDateSelected(day, 'checkIn') && !isDateSelected(day, 'checkOut') ? 'text-gray-700 hover:bg-gray-100' : ''}
                    `}
                  >
                    {day}
                  </button>
                ))}
              </div>
              
              {/* Selected dates display */}
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex items-center justify-between text-sm">
                  <div>
                    <span className="text-gray-500">Check-in:</span>
                    <span className="ml-2 font-medium text-gray-900">
                      {checkIn ? new Date(checkIn).toLocaleDateString('pt-AO', { day: 'numeric', month: 'short' }) : '—'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Check-out:</span>
                    <span className="ml-2 font-medium text-gray-900">
                      {checkOut ? new Date(checkOut).toLocaleDateString('pt-AO', { day: 'numeric', month: 'short' }) : '—'}
                    </span>
                  </div>
                </div>
              </div>
              
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  applyDates();
                }}
                disabled={!checkIn}
                className="w-full mt-4 bg-gradient-to-r from-[#E8513D] to-[#D14430] text-white rounded-xl py-3 text-sm font-semibold hover:shadow-lg hover:shadow-[#E8513D]/25 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('search.applyDates')}
              </button>
            </div>
          )}
        </div>

        <div className="w-px h-8 bg-gray-200 self-center hidden sm:block" />

        {/* HÓSPEDES - Guests */}
        <div 
          ref={guestDropdownRef}
          className="flex-1 min-w-[110px] px-4 flex flex-col justify-center cursor-pointer transition-all duration-200 hover:bg-gray-50/80 relative hidden md:flex"
          onClick={toggleGuestDropdown}
        >
          <label className="block text-[11px] font-bold text-[#E8513D] uppercase tracking-wider mb-1">
            {t('home.search.guests')}
          </label>
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <span className="text-[15px] text-gray-600 truncate">
              {guests} {guests === 1 ? t('search.guest') : t('search.guests')}
            </span>
            <ChevronDown className={`w-3.5 h-3.5 text-gray-400 ml-auto transition-transform duration-200 ${showGuestDropdown ? 'rotate-180' : ''}`} />
          </div>
          
          {/* Guest Dropdown */}
          {showGuestDropdown && (
            <div className="absolute top-[calc(100%+12px)] left-1/2 -translate-x-1/2 w-[280px] bg-white rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] border border-gray-100 p-5 z-[100] animate-in fade-in zoom-in-95 duration-200">
              <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white border-l border-t border-gray-100 rotate-45" />
              
              <div className="flex items-center justify-between mb-1">
                <span className="text-base font-semibold text-gray-900">{t('home.search.guests')}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setGuests(2);
                  }}
                  className="text-sm text-gray-400 hover:text-[#E8513D] transition-colors"
                >
                  {t('search.clear')}
                </button>
              </div>
              
              <div className="flex items-center justify-between py-4 border-t border-gray-100">
                <span className="text-sm text-gray-600">{t('search.totalGuests')}</span>
                <div className="flex items-center gap-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setGuests(Math.max(1, guests - 1));
                    }}
                    disabled={guests <= 1}
                    className="w-9 h-9 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:border-[#E8513D] hover:text-[#E8513D] transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-gray-300 disabled:hover:text-gray-600"
                  >
                    <span className="text-lg leading-none">−</span>
                  </button>
                  <span className="w-8 text-center text-base font-semibold text-gray-900">{guests}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setGuests(Math.min(16, guests + 1));
                    }}
                    disabled={guests >= 16}
                    className="w-9 h-9 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:border-[#E8513D] hover:text-[#E8513D] transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-gray-300 disabled:hover:text-gray-600"
                  >
                    <span className="text-lg leading-none">+</span>
                  </button>
                </div>
              </div>
              
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowGuestDropdown(false);
                }}
                className="w-full mt-2 bg-gradient-to-r from-[#E8513D] to-[#D14430] text-white rounded-xl py-3 text-sm font-semibold hover:shadow-lg hover:shadow-[#E8513D]/25 transition-all duration-200"
              >
                Aplicar
              </button>
            </div>
          )}
        </div>

        <div className="w-px h-8 bg-gray-200 self-center hidden md:block" />

        {/* PROVÍNCIA - Province */}
        <div 
          ref={provinceDropdownRef}
          className="flex-1 min-w-[110px] px-4 flex flex-col justify-center cursor-pointer transition-all duration-200 hover:bg-gray-50/80 relative hidden lg:flex"
          onClick={toggleProvinceDropdown}
        >
          <label className="block text-[11px] font-bold text-[#E8513D] uppercase tracking-wider mb-1">
            PROVÍNCIA
          </label>
          <div className="flex items-center gap-2">
            <span className="text-[15px] text-gray-600 truncate">{getProvinceName()}</span>
            <ChevronDown className={`w-3.5 h-3.5 text-gray-400 ml-auto transition-transform duration-200 ${showProvinceDropdown ? 'rotate-180' : ''}`} />
          </div>
          
          {/* Province Dropdown */}
          {showProvinceDropdown && (
            <div className="absolute top-[calc(100%+12px)] left-1/2 -translate-x-1/2 w-[220px] bg-white rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] border border-gray-100 py-3 z-[100] animate-in fade-in zoom-in-95 duration-200 max-h-[320px] overflow-y-auto">
              <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white border-l border-t border-gray-100 rotate-45" />
              
              {provinces.map((p) => (
                <button
                  key={p.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    setProvince(p.id);
                    setShowProvinceDropdown(false);
                  }}
                  className={`w-full px-5 py-2.5 text-left text-[15px] transition-all duration-150 flex items-center justify-between group ${
                    province === p.id 
                      ? "text-[#E8513D] font-semibold bg-[#E8513D]/5" 
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {p.name}
                  {province === p.id && (
                    <div className="w-2 h-2 rounded-full bg-[#E8513D]" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="w-px h-8 bg-gray-200 self-center hidden lg:block" />

        {/* Botões de ação */}
        <div className="flex items-center gap-2 pl-3 pr-2">
          {/* Botão Pesquisar */}
          <button
            onClick={handleSearch}
            className="flex-shrink-0 bg-gradient-to-r from-[#E8513D] to-[#D14430] hover:from-[#D14430] hover:to-[#E8513D] text-white rounded-full px-6 py-3 flex items-center gap-2 transition-all duration-200 shadow-md hover:shadow-xl hover:shadow-[#E8513D]/30 active:scale-95"
          >
            <Search className="w-[18px] h-[18px]" />
            <span className="font-semibold text-[15px] hidden lg:inline">{t('search.searchButton')}</span>
          </button>

          {/* Botão Filtro */}
          <button
            onClick={handleFilterClick}
            className="flex-shrink-0 w-11 h-11 rounded-full border border-gray-200 hover:border-[#E8513D] hover:bg-[#E8513D]/5 transition-all duration-200 flex items-center justify-center active:scale-95 group"
            title={t('search.advancedFilters')}
          >
            <SlidersHorizontal className="w-[18px] h-[18px] text-gray-500 group-hover:text-[#E8513D] transition-colors" />
          </button>

          {/* Botão Limpar (Reset) - só aparece quando há valores */}
          {hasAnyValue && (
            <button
              onClick={handleResetAll}
              className="flex-shrink-0 w-11 h-11 rounded-full border border-gray-200 hover:border-[#E8513D] hover:bg-[#E8513D]/5 transition-all duration-200 flex items-center justify-center active:scale-95 group animate-in fade-in zoom-in-95 duration-200"
              title={t('search.clearAllFields')}
              aria-label={t('search.clearSearch')}
            >
              <RotateCcw className="w-[18px] h-[18px] text-gray-500 group-hover:text-[#E8513D] transition-colors" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
